// TunnelPanel.js
// Admin UI component for managing port forwarding tunnels via WebSocket
import React, { useState, useRef, useEffect, Fragment } from 'react';
import { Box, Button, Typography, Grid, TextField, CircularProgress, Alert, Card, CardContent, IconButton, Chip, List, ListItem, ListItemText, ListItemSecondaryAction, Divider, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import LanIcon from '@mui/icons-material/Lan';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { v4 as uuidv4 } from 'uuid';
import { API_BASE_URL, getTunnels, deleteTunnel } from './api';

export default function TunnelPanel({ apiKey, clientId, clientName, onError, onTunnelClosed }) {
    const [correlationId, setCorrelationId] = useState(null);
    const [tunnelId, setTunnelId] = useState(null);
    const [serverPort, setServerPort] = useState('');
    const [targetHost, setTargetHost] = useState('');
    const [targetPort, setTargetPort] = useState('');
    const [lifetime, setLifetime] = useState(15);
    const [status, setStatus] = useState('');
    const [error, setError] = useState('');
    const [connecting, setConnecting] = useState(false);
    const [active, setActive] = useState(false);    const [connections, setConnections] = useState([]); // List of active connections
    const [activeTunnels, setActiveTunnels] = useState([]); // List of active tunnels from API
    const [loadingTunnels, setLoadingTunnels] = useState(false);
    const [refreshing, setRefreshing] = useState(false);    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [tunnelToDelete, setTunnelToDelete] = useState(null);
    const websocketRef = useRef(null);

    // Cleanup on unmount
    useEffect(() => {
        loadActiveTunnels(); // Load tunnels when component mounts
        
        // Auto-refresh tunnels every 30 seconds
        const refreshInterval = setInterval(() => {
            if (!refreshing && !loadingTunnels) {
                loadActiveTunnels();
            }
        }, 30000);
        
        // Add a safety timeout to prevent infinite loading
        const loadingTimeout = setTimeout(() => {
            if (loadingTunnels) {
                console.warn('Tunnel loading timed out after 10 seconds');
                setLoadingTunnels(false);
                setError('Loading tunnels timed out. Please try refreshing.');
            }
        }, 10000);
        
        return () => {
            cleanupConnection();
            clearInterval(refreshInterval);
            clearTimeout(loadingTimeout);
        };    }, []);
    
    // Load active tunnels from API
    const loadActiveTunnels = async () => {
        setLoadingTunnels(true);
        setError(''); // Clear any previous errors
        try {
            console.log('Loading tunnels for client:', clientId);
            const tunnels = await getTunnels(apiKey);
            console.log('Received tunnels:', tunnels);
            
            // Handle case where API returns non-array or undefined
            if (!Array.isArray(tunnels)) {
                console.warn('API returned non-array for tunnels:', tunnels);
                setActiveTunnels([]);
                return;
            }
            
            // Filter tunnels for this specific client
            const clientTunnels = tunnels.filter(tunnel => tunnel.clientID === clientId);
            console.log('Filtered tunnels for client:', clientTunnels);
            setActiveTunnels(clientTunnels);
        } catch (err) {
            console.error('Failed to load active tunnels:', err);
            setError('Failed to load active tunnels: ' + err.message);
        } finally {
            setLoadingTunnels(false);
        }
    };

    // Refresh tunnels manually
    const refreshTunnels = async () => {
        setRefreshing(true);
        await loadActiveTunnels();
        setRefreshing(false);
    };    // Delete a tunnel via API
    const handleDeleteTunnel = async (tunnelIdToDelete) => {
        try {
            await deleteTunnel(apiKey, tunnelIdToDelete);
            // Refresh the list after deletion
            await loadActiveTunnels();
            setStatus(`Tunnel ${tunnelIdToDelete} deleted successfully`);
            setDeleteDialogOpen(false);
            setTunnelToDelete(null);
        } catch (err) {
            console.error('Failed to delete tunnel:', err);
            setError('Failed to delete tunnel: ' + err.message);
            setDeleteDialogOpen(false);
            setTunnelToDelete(null);
        }
    };

    // Open delete confirmation dialog
    const openDeleteDialog = (tunnel) => {
        setTunnelToDelete(tunnel);
        setDeleteDialogOpen(true);
    };

    // Close delete confirmation dialog
    const closeDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setTunnelToDelete(null);
    };

    // Format time helpers
    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };    const formatTimeRemaining = (expiresAt) => {
        if (!expiresAt) return 'Indefinite';
        const now = new Date();
        const expiry = new Date(expiresAt);
        const diffMs = expiry.getTime() - now.getTime();
        
        if (diffMs <= 0) return 'Expired';
        
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMinutes / 60);
        
        if (diffHours > 0) {
            return `${diffHours}h ${diffMinutes % 60}m`;
        }
        return `${diffMinutes}m`;
    };

    const isExpired = (expiresAt) => {
        if (!expiresAt) return false;
        return new Date(expiresAt).getTime() <= Date.now();
    };
    
    // Clean up function to handle connection termination
    const cleanupConnection = () => {
        if (websocketRef.current) {
            console.log('Closing tunnel WebSocket connection');
            websocketRef.current.close();
            websocketRef.current = null;
        }
        
        setConnecting(false);
    };

    const startTunnel = async () => {
        setConnecting(true);
        setError('');
        setStatus('');
        setTunnelId(null);
        setCorrelationId(null);
        setConnections([]);
        
        // Clean up any existing connection
        cleanupConnection();
        
        try {
            // Fetch WebSocket ticket
            console.log('Fetching WebSocket ticket...');
            const ticketResp = await fetch(`${API_BASE_URL}/api/auth/ws-ticket`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiKey}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!ticketResp.ok) {
                throw new Error(`Failed to obtain WebSocket ticket: ${ticketResp.status} ${ticketResp.statusText}`);
            }
            
            const ticketData = await ticketResp.json();
            if (!ticketData.ticket) {
                throw new Error('No ticket received from server');
            }
            
            const ticket = ticketData.ticket;
            
            // Determine WebSocket URL from API base URL
            let wsScheme = 'ws';
            let wsHost = API_BASE_URL.split('//')[1]; // Get host and potential port from API_BASE_URL
            if (API_BASE_URL.startsWith('https')) {
                wsScheme = 'wss';
            }
            // If API_BASE_URL includes a path, remove it for WebSocket host
            if (wsHost.includes('/')) {
                wsHost = wsHost.substring(0, wsHost.indexOf('/'));
            }
            const wsUrl = `${wsScheme}://${wsHost}/ws/admin?ticket=${ticket}`;
            
            console.log('Connecting to WebSocket with ticket...');
            const wsConn = new window.WebSocket(wsUrl);
            websocketRef.current = wsConn;
            
            const corrId = uuidv4();
            setCorrelationId(corrId);

            wsConn.onopen = () => {
                console.log('WebSocket connection established, sending tunnel request');
                setConnecting(false);
                setActive(true);
                setStatus('Tunnel request sent...');
                
                wsConn.send(JSON.stringify({
                    action: 'admin_create_tunnel_request',
                    payload: {
                        correlationID: corrId,
                        targetClientID: clientId,
                        serverListenPort: parseInt(serverPort, 10),
                        targetHost,
                        targetPort: parseInt(targetPort, 10),
                        lifetimeMinutes: parseInt(lifetime, 10)
                    }
                }));
            };
            
            wsConn.onmessage = (event) => {
                try {
                    const msg = JSON.parse(event.data);
                    console.log('Received message:', msg.type);
                      if (msg.type === 'admin_event_tunnel_ready') {
                        if (msg.payload.correlationID === corrId) {
                            setTunnelId(msg.payload.tunnelID);
                            setStatus(`Tunnel ready on port ${msg.payload.serverListenPort}`);
                            // Refresh tunnels list to show the new tunnel
                            loadActiveTunnels();
                        }
                    } else if (msg.type === 'admin_event_tunnel_failed') {
                        setError(msg.payload.error || 'Tunnel failed');
                        setActive(false);
                        setStatus('');
                    } else if (msg.type === 'admin_event_tunnel_closed') {
                        setStatus('Tunnel closed');
                        setActive(false);
                        setTunnelId(null);
                        // Refresh tunnels list to remove the closed tunnel
                        loadActiveTunnels();
                        if (onTunnelClosed) onTunnelClosed();
                    } else if (msg.type === 'client_event_tunnel_new_connection') {
                        setConnections((prev) => [...prev, msg.payload.connectionID]);
                    } else if (msg.type === 'client_event_tunnel_connection_closed') {
                        setConnections((prev) => prev.filter(id => id !== msg.payload.connectionID));
                    }
                } catch (e) {
                    setError('Failed to parse tunnel message');
                }
            };
            
            wsConn.onerror = (err) => {
                console.error("WebSocket error:", err);
                setError('WebSocket connection error');
                setConnecting(false);
                setActive(false);
                if (onError) onError('WebSocket connection error');
                cleanupConnection();
            };
            
            wsConn.onclose = (event) => {
                console.log(`WebSocket closed with code ${event.code}. Reason: ${event.reason || 'No reason provided'}`);
                setActive(false);
                setConnecting(false);
                websocketRef.current = null;
            };
        } catch (err) {
            console.error("Failed to establish tunnel:", err);
            setError('Failed to start tunnel: ' + err.message);
            setConnecting(false);
            if (onError) onError('Failed to start tunnel: ' + err.message);
        }
    };    const closeTunnel = async () => {
        if (websocketRef.current && tunnelId) {
            websocketRef.current.send(JSON.stringify({
                action: 'admin_close_tunnel_request',
                payload: { tunnelID: tunnelId }
            }));
        }
        setActive(false);
        setTunnelId(null);
        setCorrelationId(null);
        setStatus('Tunnel closed');
        setConnections([]);
        cleanupConnection();
        
        // Refresh the tunnels list after closing
        await loadActiveTunnels();
        
        if (onTunnelClosed) onTunnelClosed();
    };
    
    return (
        <Box>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LanIcon color="primary" />
                <Typography variant="h6">Port Forwarding Tunnel</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {clientName ? `Connected to ${clientName}` : `Connected to ${clientId}`}
                </Typography>
            </Box>

            <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={3}>
                    <TextField
                        label="Server Listen Port"
                        value={serverPort}
                        onChange={e => setServerPort(e.target.value)}
                        size="small"
                        fullWidth
                        disabled={active || connecting}
                        placeholder="20000-30000"
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Target Host"
                        value={targetHost}
                        onChange={e => setTargetHost(e.target.value)}
                        size="small"
                        fullWidth
                        disabled={active || connecting}
                        placeholder="127.0.0.1 or LAN IP"
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField
                        label="Target Port"
                        value={targetPort}
                        onChange={e => setTargetPort(e.target.value)}
                        size="small"
                        fullWidth
                        disabled={active || connecting}
                        placeholder="e.g. 22"
                    />
                </Grid>
                <Grid item xs={12} sm={2}>
                    <TextField
                        label="Lifetime (min)"
                        value={lifetime}
                        onChange={e => setLifetime(e.target.value)}
                        size="small"
                        fullWidth
                        disabled={active || connecting}
                    />
                </Grid>
                <Grid item xs={12}>
                    {active ? (
                        <Button
                            variant="contained"
                            color="error"
                            onClick={closeTunnel}
                            startIcon={<StopIcon />}
                            fullWidth
                        >
                            Close Tunnel
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={startTunnel}
                            disabled={connecting || !serverPort || !targetHost || !targetPort}
                            startIcon={<PlayArrowIcon />}
                            fullWidth
                        >
                            {connecting ? 'Creating tunnel...' : 'Create Tunnel'}
                        </Button>
                    )}
                </Grid>
            </Grid>

            <Box mb={2}>
                <Typography variant="body2" fontWeight={500} color={active ? "success.main" : "text.secondary"}>
                    {status || (active ? 'Tunnel active.' : 'No active tunnel. Configure and click "Create Tunnel" to begin.')}
                </Typography>
            </Box>

            {active && connections.length > 0 && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" gutterBottom>Active Connections:</Typography>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {connections.map(connId => (
                            <li key={connId}>
                                <Typography variant="body2">{connId}</Typography>
                            </li>
                        ))}
                    </ul>
                </Box>
            )}

            {connecting && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Setting up tunnel...</Typography>
                </Box>
            )}
              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

            {/* Active Tunnels Section */}
            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LanIcon color="primary" />
                            Active Tunnels for {clientName || clientId}
                            {activeTunnels.length > 0 && (
                                <Chip 
                                    label={activeTunnels.length} 
                                    size="small" 
                                    color="primary" 
                                    variant="outlined"
                                />
                            )}
                        </Typography>
                        <IconButton 
                            onClick={refreshTunnels} 
                            disabled={refreshing || loadingTunnels}
                            size="small"
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Box>

                    {loadingTunnels && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2">Loading active tunnels...</Typography>
                        </Box>
                    )}

                    {!loadingTunnels && activeTunnels.length === 0 && (
                        <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                            <Typography variant="body2">
                                No active tunnels found for this client.
                            </Typography>
                        </Box>
                    )}

                    {!loadingTunnels && activeTunnels.length > 0 && (
                        <List>
                            {activeTunnels.map((tunnel, index) => (
                                <React.Fragment key={tunnel.tunnelID}>
                                    <ListItem sx={{ px: 0 }}>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <Typography variant="subtitle1" component="span">
                                                        Port {tunnel.serverListenPort} → {tunnel.targetHost}:{tunnel.targetPort}
                                                    </Typography>                                    <Chip 
                                                        label={tunnel.isListenerActive ? 'Active' : 'Inactive'} 
                                                        size="small"
                                                        color={tunnel.isListenerActive ? 'success' : 'error'}
                                                        variant="outlined"
                                                    />
                                                    {isExpired(tunnel.expiresAt) && (
                                                        <Chip 
                                                            label="Expired" 
                                                            size="small"
                                                            color="warning"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                    {tunnel.connectionCount > 0 && (
                                                        <Chip 
                                                            label={`${tunnel.connectionCount} connections`} 
                                                            size="small"
                                                            color="info"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Tunnel ID:</strong> {tunnel.tunnelID}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Created:</strong> {formatDateTime(tunnel.createdAt)}
                                                    </Typography>                                                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <AccessTimeIcon fontSize="small" />
                                                        <strong>Expires:</strong> 
                                                        <span style={{ 
                                                            color: isExpired(tunnel.expiresAt) ? '#f44336' : 
                                                                   (tunnel.expiresAt && new Date(tunnel.expiresAt).getTime() - Date.now() < 5 * 60 * 1000) ? '#ff9800' : 'inherit' 
                                                        }}>
                                                            {formatTimeRemaining(tunnel.expiresAt)}
                                                        </span>
                                                        {tunnel.expiresAt && (
                                                            <span style={{ marginLeft: 4 }}>
                                                                ({formatDateTime(tunnel.expiresAt)})
                                                            </span>
                                                        )}
                                                    </Typography>
                                                    {tunnel.adminInitiatorID && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            <strong>Initiated by:</strong> {tunnel.adminInitiatorID}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            }
                                        />
                                        <ListItemSecondaryAction>                                            <IconButton 
                                                edge="end" 
                                                aria-label="delete"
                                                onClick={() => openDeleteDialog(tunnel)}
                                                color="error"
                                                size="small"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                    {index < activeTunnels.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    )}                </CardContent>
            </Card>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={closeDeleteDialog}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Delete Tunnel Confirmation
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete this tunnel?
                        {tunnelToDelete && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2">
                                    <strong>Tunnel ID:</strong> {tunnelToDelete.tunnelID}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Port:</strong> {tunnelToDelete.serverListenPort} → {tunnelToDelete.targetHost}:{tunnelToDelete.targetPort}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Status:</strong> {tunnelToDelete.isListenerActive ? 'Active' : 'Inactive'}
                                </Typography>
                                {tunnelToDelete.connectionCount > 0 && (
                                    <Typography variant="body2" color="warning.main">
                                        <strong>Warning:</strong> This tunnel has {tunnelToDelete.connectionCount} active connections that will be terminated.
                                    </Typography>
                                )}
                            </Box>
                        )}
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} color="primary">
                        Cancel
                    </Button>
                    <Button 
                        onClick={() => handleDeleteTunnel(tunnelToDelete?.tunnelID)} 
                        color="error" 
                        variant="contained"
                        autoFocus
                    >
                        Delete Tunnel
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
